import React, { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const NAV_LINKS = [
  { label: "Services", href: "/services" },
  { label: "Products", href: "/products" },
  { label: "Insights", href: "/insights" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export default function MainLayout() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLogo = scrolled
    ? "/images/logo-blue.png"
    : "/images/logo-white.png";

  return (
    <div className="min-h-screen bg-white font-sans text-itl-navy">
      {/* ── NAV ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-[0_1px_0_0_#e5e7eb]" : "bg-transparent"
        }`}
      >
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img
              src={navLogo}
              alt="Incubator Tech Limited"
              className="h-9 w-auto object-contain transition-all duration-300"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-[13px] font-medium tracking-wide uppercase transition-colors duration-200 border-b-2 border-transparent hover:border-itl-blue pb-0.5 ${
                  scrolled
                    ? "text-itl-navy hover:text-itl-blue"
                    : "text-white/90 hover:text-white hover:border-white"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="ml-4 px-5 py-2.5 bg-itl-blue text-white text-[12px] font-semibold uppercase tracking-widest hover:bg-itl-navy transition-colors duration-200"
            >
              Work With Us
            </Link>
          </nav>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen((p) => !p)}
            className={`lg:hidden flex flex-col gap-[5px] p-2 ${
              scrolled ? "text-itl-navy" : "text-white"
            }`}
            aria-label="Toggle menu"
          >
            <span
              className={`block h-[2px] w-6 bg-current transition-all duration-300 ${
                menuOpen ? "rotate-45 translate-y-[7px]" : ""
              }`}
            />
            <span
              className={`block h-[2px] w-6 bg-current transition-all duration-300 ${
                menuOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block h-[2px] w-6 bg-current transition-all duration-300 ${
                menuOpen ? "-rotate-45 -translate-y-[7px]" : ""
              }`}
            />
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden bg-white border-t border-gray-100 overflow-hidden"
            >
              <div className="px-6 py-6 flex flex-col gap-5">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-[13px] font-semibold uppercase tracking-widest text-itl-navy hover:text-itl-blue transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/contact"
                  className="mt-2 px-5 py-3 bg-itl-blue text-white text-[12px] font-semibold uppercase tracking-widest text-center hover:bg-itl-navy transition-colors"
                >
                  Work With Us
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── PAGE CONTENT ── */}
      <main>
        <Outlet />
      </main>

      {/* ── FOOTER ── */}
      <footer className="bg-itl-navy text-white">
        {/* CTA band */}
        <div className="border-b border-white/10">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-itl-blue mb-3">
                Let's build something great
              </p>
              <h2 className="font-serif text-3xl lg:text-4xl font-light leading-snug max-w-lg">
                Turn your boldest idea into a world-class digital product.
              </h2>
            </div>
            <Link
              to="/contact"
              className="shrink-0 px-8 py-4 border border-white text-white text-[12px] font-semibold uppercase tracking-widest hover:bg-white hover:text-itl-navy transition-colors duration-200"
            >
              Start a Conversation
            </Link>
          </div>
        </div>

        {/* Links grid */}
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
          <div>
            <img
              src="/images/logo-white.png"
              alt="Incubator Tech Limited"
              className="h-8 w-auto object-contain mb-4"
            />
            <p className="text-white/50 text-[13px] leading-relaxed">
              World-class web &amp; mobile solutions built for resilience.
            </p>
          </div>

          {[
            {
              heading: "Services",
              links: [
                { label: "Web Development", href: "/services#web" },
                { label: "Mobile Apps", href: "/services#mobile" },
                { label: "Tech Products", href: "/products" },
                { label: "Consulting", href: "/services#consulting" },
              ],
            },
            {
              heading: "Company",
              links: [
                { label: "About Us", href: "/about" },
                { label: "Insights", href: "/insights" },
                { label: "Careers", href: "/careers" },
                { label: "Contact", href: "/contact" },
              ],
            },
            {
              heading: "Connect",
              links: [
                { label: "LinkedIn", href: "#" },
                { label: "Twitter / X", href: "#" },
                { label: "GitHub", href: "#" },
                {
                  label: "hello@incubatortech.ng",
                  href: "mailto:hello@incubatortech.ng",
                },
              ],
            },
          ].map((col) => (
            <div key={col.heading}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40 mb-4">
                {col.heading}
              </p>
              <ul className="flex flex-col gap-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.href}
                      className="text-[13px] text-white/70 hover:text-white transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/30 text-[11px]">
              © {new Date().getFullYear()} Incubator Tech Limited. All rights
              reserved.
            </p>
            <div className="flex gap-6">
              {["Privacy Policy", "Terms of Use", "Cookies"].map((t) => (
                <Link
                  key={t}
                  to="#"
                  className="text-white/30 text-[11px] hover:text-white/60 transition-colors"
                >
                  {t}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}